import type { RegionalSalesDecodeResult, RegionalSalesQuery, VendorStores } from './regional-sales';

/**
 * Every chain's footprint, as one index read decoded it.
 *
 * `failed` separates the two ways this can carry no chains. A read that failed knows nothing
 * about the country's stores, and a map drawn without them is not a map with no shops in it --
 * it is a map that never learned where they are. Callers short-circuit on the first and paint
 * the second, and `vendors.length` cannot tell them apart on its own.
 */
export type StoreIndexReadResult = {
  /** True iff the read itself did not complete. The chains below are then meaningless. */
  readonly failed: boolean;
  readonly vendors: readonly VendorStores[];
};

/** What every failed index read yields: no chains, never a partial or faked one. */
export const FAILED_STORE_INDEX_READ: StoreIndexReadResult = { failed: true, vendors: [] };

/**
 * One regional read, with row presence kept separate from payload emptiness.
 *
 * The three states a caller must tell apart are: no row for these parameters (nothing has
 * been aggregated yet -- warm it), a row whose payload is empty (aggregated, and the answer
 * is genuinely "no sales" -- do not warm it again), and a row with facts. `facts.length`
 * cannot make that distinction on its own, which is why `hasPayload` exists.
 */
export type RegionalSalesReadResult = {
  /** True iff the cache held a row for these parameters at all. */
  readonly rowExists: boolean;
  /** True iff that row carried a payload object. An empty object still counts. */
  readonly hasPayload: boolean;
  /** The row's `created_at`, for the caller's TTL decision. Null when no row, or unset. */
  readonly createdAt: string | null;
  readonly facts: readonly RegionalSalesDecodeResult[];
};

/** What every failed or missing regional read yields. */
export const EMPTY_REGIONAL_SALES_READ: RegionalSalesReadResult = {
  rowExists: false,
  hasPayload: false,
  createdAt: null,
  facts: [],
};

/**
 * Reads the map family's facts. The implementation is the only code that knows the feed's
 * tables, its cache-key construction, its key masking and its payload nesting (PRD D-03,
 * HC-02).
 *
 * Warming (submitting an aggregation job and polling it) is deliberately not here: it is a
 * write against a job queue, driven by the TTL decision this repository's `createdAt`
 * feeds, and it stays with the caller that owns that decision.
 */
export abstract class RegionalSalesRepository {
  /** Every chain's stores, for placing the map's store points. */
  abstract readStoreIndex(): Promise<StoreIndexReadResult>;

  /**
   * The aggregated facts for one query. Which cache row answers a query is the
   * implementation's business; callers describe what they want, never a key.
   */
  abstract readFacts(query: RegionalSalesQuery): Promise<RegionalSalesReadResult>;
}
